"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParticleEmitter = exports.Particle = void 0;
const Utils_1 = require("../Utils/Utils");
const GameEvent_1 = require("./GameEvent");
const pixi_js_1 = require("pixi.js");
function getRangeValue(n) {
    if (n === undefined)
        return undefined;
    if (typeof n === "number")
        return n;
    if (n instanceof Array)
        return n[(Math.random() * n.length) | 0];
    if ("max" in n)
        return n.min + Math.random() * (n.max - n.min);
    return n.base + (Math.random() * 2 - 1) * n.offset;
}
function ratio(start, end, target, from, to) {
    if (from === undefined)
        return undefined;
    if (to === undefined)
        return from;
    const r = (target - start) / (end - start);
    return (to - from) * r + from;
}
class Particle extends pixi_js_1.Sprite {
    constructor(texture) {
        super(texture);
        this.startX = 0;
        this.startY = 0;
        this.speedX = 0;
        this.speedY = 0;
        this.lifetime = 0;
        this.currentTime = 0;
        this.accelRad = 0;
        this.accelTan = 0;
        this.start = {};
        this.end = {};
        this.shouldRemove = false;
        this.setSize = (v) => this.scale.set(v);
        this.setSpin = (v) => (this.rotation = v);
        this.setR = (v) => (this.tint = Utils_1.setByte(this.tint, 2, v));
        this.setG = (v) => (this.tint = Utils_1.setByte(this.tint, 1, v));
        this.setB = (v) => (this.tint = Utils_1.setByte(this.tint, 0, v));
        this.setAlpha = (v) => (this.alpha = v);
        this.anchor.set(0.5);
    }
    setOption(option, offset) {
        if (option.blend === "add")
            this.blendMode = pixi_js_1.BLEND_MODES.ADD;
        const speed = getRangeValue(option.speed) || 0;
        const radian = getRangeValue(option.radian) || 0;
        this.speedX = speed * Math.cos(radian);
        this.speedY = speed * Math.sin(radian);
        this.lifetime = getRangeValue(option.lifeTime) || 0;
        this.gravity = option.gravity;
        this.accelRad = getRangeValue(option.accelRad) || 0;
        this.accelTan = getRangeValue(option.accelTan) || 0;
        this.startX = (getRangeValue(option.emitRect.x) || 0) + offset.x;
        this.startY = (getRangeValue(option.emitRect.y) || 0) + offset.y;
        this.x = this.startX;
        this.y = this.startY;
        this.start = {
            size: getRangeValue(option.start.size),
            spin: getRangeValue(option.start.spin),
            r: getRangeValue(option.start.r),
            g: getRangeValue(option.start.g),
            b: getRangeValue(option.start.b),
            alpha: getRangeValue(option.start.alpha),
        };
        this.end = {
            size: getRangeValue(option.end.size),
            spin: getRangeValue(option.end.spin),
            r: getRangeValue(option.end.r),
            g: getRangeValue(option.end.g),
            b: getRangeValue(option.end.b),
            alpha: getRangeValue(option.end.alpha),
        };
    }
    setRatio(prop, set) {
        const v = ratio(0, this.lifetime, this.currentTime, this.start[prop], this.end && this.end[prop]);
        if (v !== undefined)
            set(v);
    }
    // tslint:disable: no-bitwise
    /**
     *
     * @param dt in seconds
     */
    update(dt) {
        this.currentTime += dt;
        if (this.currentTime > this.lifetime) {
            this.visible = false;
            this.shouldRemove = true;
            return;
        }
        {
            this.x += this.speedX * dt;
            this.y += this.speedY * dt;
            let dvx = 0;
            let dvy = 0;
            if (this.gravity) {
                dvx += this.gravity.x;
                dvy += this.gravity.y;
            }
            if (this.accelTan) {
                dvx += this.accelTan * this.speedY;
                dvy += this.accelTan * -this.speedX;
            }
            if (this.accelRad) {
                dvx += this.accelRad * (this.x - this.startX);
                dvy += this.accelRad * (this.y - this.startY);
            }
            this.speedX += dvx * dt;
            this.speedY += dvy * dt;
        }
        {
            this.setRatio("size", this.setSize);
            this.setRatio("spin", this.setSpin);
            this.setRatio("r", this.setR);
            this.setRatio("g", this.setG);
            this.setRatio("b", this.setB);
            this.setRatio("alpha", this.setAlpha);
        }
    }
}
exports.Particle = Particle;
class ParticleEmitter extends pixi_js_1.Container {
    constructor(textures, option) {
        super();
        this.option = option;
        this.poor = new Utils_1.ObjectPool();
        this.offset = { x: 0, y: 0 };
        this.canEmit = true;
        this.currentTime = 0;
        this.counter = 0;
        this.onEmitEnd = new GameEvent_1.GameEvent();
        this.emitEnded = false;
        this.onAllEnd = new GameEvent_1.GameEvent();
        this.allEnd = false;
        this.textures = textures || [];
        this.poor.newObj = () => {
            const p = new Particle(this.textures[0]);
            this.addChild(p);
            return p;
        };
        this.poor.beforeGet = p => {
            p.visible = true;
            p.setOption(this.option, this.offset);
            p.currentTime = 0;
            const i = (Math.random() * this.textures.length) | 0;
            p.texture = this.textures[i];
        };
        this.poor.beforeSave = p => {
            p.shouldRemove = false;
            p.visible = false;
        };
    }
    /**
     *
     * @param dt in seconds
     */
    update(dt) {
        this.currentTime += dt;
        if (this._destroyed || this.currentTime < 0)
            return;
        if (this.currentTime - dt < 0)
            this.currentTime = 0;
        if (this.canEmit && (this.option.duration <= 0 || this.currentTime - dt < this.option.duration)) {
            const time = this.option.duration > 0
                ? this.currentTime < this.option.duration
                    ? dt
                    : this.option.duration - this.currentTime + dt
                : dt;
            this.counter += time * this.option.emissionRate;
            while (this.counter > 1) {
                this.counter -= 1;
                this.poor.get();
            }
        }
        let visibleCount = 0;
        for (let i = 0; i < this.children.length; i++) {
            const p = this.children[i];
            if (!p.visible)
                continue;
            p.update(dt);
            if (p.shouldRemove)
                this.poor.save(p);
            else
                visibleCount++;
        }
        if (!this.canEmit || (this.option.duration > 0 && this.currentTime >= this.option.duration)) {
            if (!this.emitEnded)
                this.onEmitEnd.emit();
            this.emitEnded = true;
        }
        else {
            this.emitEnded = false;
        }
        if (visibleCount <= 0 && this.emitEnded) {
            if (!this.allEnd)
                this.onAllEnd.emit();
            this.allEnd = true;
        }
        else {
            this.allEnd = false;
        }
    }
}
exports.ParticleEmitter = ParticleEmitter;
//# sourceMappingURL=ParticleEmitter.js.map